import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Input, Select, Field, cx } from '../ui/WorkspaceUI';
import {
  ATTACHMENT_KINDS,
  DATE_RANGES,
  EMPTY_FIELDS,
  OPERATOR_HELP,
  SEARCH_CATEGORIES,
  SEARCH_SCOPES,
  buildQuery,
  hasAnyField,
  parseQueryFields,
} from '../../utils/mailSearchQuery';

/**
 * Advanced mail search, as a dropdown under the header search box.
 *
 * The form is a front-end for the operator language rather than a separate API:
 * everything it produces is a normal query string, so the same search can be
 * typed by hand, bookmarked, or edited in the search box afterwards. The live
 * preview at the bottom is what teaches the syntax.
 */
const SearchOptionsPanel = ({ query = '', onSearch, onClose }) => {
  const [fields, setFields] = useState(() => parseQueryFields(query));
  const [showSyntax, setShowSyntax] = useState(false);
  const panelRef = useRef(null);
  const firstFieldRef = useRef(null);

  // Reopening on a different query should show that query, not stale state.
  useEffect(() => {
    setFields(parseQueryFields(query));
  }, [query]);

  useEffect(() => {
    firstFieldRef.current?.focus();
    const onKey = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose?.();
      }
    };
    const onPointerDown = (event) => {
      if (!panelRef.current?.contains(event.target)) onClose?.();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [onClose]);

  const set = (key) => (event) => {
    const target = event.target;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    setFields((prev) => ({ ...prev, [key]: value }));
  };

  const preview = useMemo(() => buildQuery(fields), [fields]);
  const canSearch = hasAnyField(fields);

  const submit = (event) => {
    event.preventDefault();
    if (!canSearch) return;
    onSearch?.(preview);
  };

  return (
    <form
      ref={panelRef}
      onSubmit={submit}
      role="dialog"
      aria-label="Search options"
      className={cx(
        'ws-glass ws-scroll absolute left-0 right-0 top-full z-[1200] mt-1.5 max-h-[70vh]',
        'animate-ws-fade-up rounded-xl p-4 text-left shadow-glass-lg'
      )}
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="From">
          <Input ref={firstFieldRef} value={fields.from} onChange={set('from')} placeholder="alice@example.com" />
        </Field>
        <Field label="To">
          <Input value={fields.to} onChange={set('to')} placeholder="bob@atozasmail.in" />
        </Field>
        <Field label="Subject" className="sm:col-span-2">
          <Input value={fields.subject} onChange={set('subject')} placeholder="Contains these words" />
        </Field>
        <Field label="Has the words">
          <Input value={fields.hasWords} onChange={set('hasWords')} placeholder="invoice quarterly" />
        </Field>
        <Field label="Doesn't have">
          <Input value={fields.notWords} onChange={set('notWords')} placeholder="unsubscribe" />
        </Field>

        <Field label="Search in">
          <Select value={fields.scope} onChange={set('scope')} className="w-full">
            {SEARCH_SCOPES.map((scope) => (
              <option key={scope.value} value={scope.value}>
                {scope.label}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Category">
          <Select value={fields.category} onChange={set('category')} className="w-full">
            {SEARCH_CATEGORIES.map((category) => (
              <option key={category.value} value={category.value}>
                {category.label}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Label">
          <Input value={fields.label} onChange={set('label')} placeholder="Finance" />
        </Field>
        <Field label="Attachment name">
          <Input value={fields.filename} onChange={set('filename')} placeholder="invoice.pdf" />
        </Field>

        <Field label="Attachment type">
          <Select value={fields.attachmentKind} onChange={set('attachmentKind')} className="w-full">
            {ATTACHMENT_KINDS.map((kind) => (
              <option key={kind.value} value={kind.value}>
                {kind.label}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Date">
          <Select value={fields.olderThan} onChange={set('olderThan')} className="w-full">
            {DATE_RANGES.map((range) => (
              <option key={range.value} value={range.value}>
                {range.label}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="After" hint="On or after this date">
          <Input type="date" value={fields.after} onChange={set('after')} />
        </Field>
        <Field label="Before" hint="Strictly before this date">
          <Input type="date" value={fields.before} onChange={set('before')} />
        </Field>

        <Field label="Larger than" hint="e.g. 5M, 500k">
          <Input value={fields.largerThan} onChange={set('largerThan')} placeholder="5M" />
        </Field>
        <Field label="Smaller than" hint="e.g. 1M">
          <Input value={fields.smallerThan} onChange={set('smallerThan')} placeholder="1M" />
        </Field>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-hairline pt-3">
        {[
          ['attachment', 'Has attachment'],
          ['isUnread', 'Unread only'],
          ['isStarred', 'Starred only'],
          ['isImportant', 'Important only'],
        ].map(([key, label]) => (
          <label key={key} className="flex cursor-pointer items-center gap-2 text-[13px] text-ink">
            <input
              type="checkbox"
              checked={!!fields[key]}
              onChange={set(key)}
              disabled={key === 'attachment' && !!fields.attachmentKind}
              className="ws-focus h-4 w-4 accent-[var(--ws-accent)]"
            />
            {label}
          </label>
        ))}
      </div>

      <div className="mt-3 border-t border-hairline pt-3">
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs font-medium uppercase tracking-wide text-ink-soft">Query</span>
          <button
            type="button"
            onClick={() => setShowSyntax((v) => !v)}
            className="ws-focus rounded text-xs font-medium text-accent hover:underline"
          >
            {showSyntax ? 'Hide syntax reference' : 'Syntax reference'}
          </button>
        </div>
        <code className="mt-1.5 block break-words rounded-lg bg-muted px-3 py-2 font-mono text-xs text-ink">
          {preview || 'Fill in a field to build a query'}
        </code>

        {showSyntax && (
          <dl className="mt-3 grid grid-cols-1 gap-x-6 gap-y-1.5 sm:grid-cols-2">
            {OPERATOR_HELP.map((item) => (
              <div key={item.syntax} className="flex items-baseline gap-2">
                <dt className="shrink-0 font-mono text-xs text-accent">{item.syntax}</dt>
                <dd className="min-w-0 truncate text-xs text-ink-soft">{item.description}</dd>
              </div>
            ))}
          </dl>
        )}
      </div>

      <div className="mt-4 flex items-center justify-end gap-2">
        <Button variant="ghost" onClick={() => setFields({ ...EMPTY_FIELDS })}>
          Clear
        </Button>
        <Button type="submit" variant="primary" disabled={!canSearch}>
          Search
        </Button>
      </div>
    </form>
  );
};

export default SearchOptionsPanel;
